import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/common';
import DiscountVoucherForm from '../DiscountVoucherForm';
import {
  CreateDiscountVoucherRequest,
  UpdateDiscountVoucherRequest,
  DiscountVoucherUI,
} from '@/types/discountVoucher';
import { discountVoucherApi } from '@/api/endpoints/discountVouchers';
import { toast } from '@/store/toastStore';
import { useUIStore } from '@/store/uiStore';
import { Customer } from '@/types/customer';

interface CreateDiscountVoucherModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Called with the created voucher - useful for chaining to confirm modal */
  onCreated?: (voucher: DiscountVoucherUI) => void;
  initialCustomer?: Customer;
}

const CreateDiscountVoucherModal: React.FC<CreateDiscountVoucherModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onCreated,
  initialCustomer,
}) => {
  const { t } = useTranslation();
  const { refreshCallbacks } = useUIStore();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (
    data: CreateDiscountVoucherRequest | UpdateDiscountVoucherRequest,
  ) => {
    setLoading(true);
    try {
      const createdVoucher = await discountVoucherApi.createDiscountVoucher(data as CreateDiscountVoucherRequest);
      toast.success(t('discountVoucher.messages.createSuccess', 'Tạo phiếu chiết khấu thành công'));

      refreshCallbacks.discountVouchers?.();
      onSuccess?.();
      onCreated?.(createdVoucher);
      onClose();
    } catch (error: any) {
      console.error('Error creating discount voucher:', error);
      toast.error(
        error?.response?.data?.message ||
          t('discountVoucher.messages.createError', 'Không tạo được phiếu chiết khấu'),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('discountVoucher.modals.create.title', 'Tạo phiếu chiết khấu')}
      size="2xl"
    >
      <div className="p-6">
        <DiscountVoucherForm
          mode="create"
          onSubmit={handleSubmit}
          onCancel={handleClose}
          loading={loading}
          initialCustomer={initialCustomer}
        />
      </div>
    </Modal>
  );
};

export default CreateDiscountVoucherModal;
